import { memo, useEffect, useState } from 'react';
import * as Y from 'yjs';
import type { NodeProps, Node } from '@xyflow/react';
import styles from './component.module.css';
import fmtStyles from '../formatting.module.css';
import type { StickyNodeData } from './types';
import { makeDefault } from './defaults';
import { withDefaults } from '../with-defaults';
import { SelectionTransformer } from '@/features/canvas/engine/selection/selection-transformer';
import { useNodeActions } from "@/features/canvas/engine/wiring/contexts/node-actions-context";
import { useCanvasDoc } from "@/features/canvas/engine/wiring/contexts/canvas-doc-context";
import { formattingVars } from '../../hooks/use-formatting-state';
import { resolveNodeAriaLabel } from '../../a11y/aria-labels';
import { useParentLabelResolver } from '../../a11y/parent-label-context';
import { NodeTextEditor } from '../node-text-editor';

type StickyNodeShape = Node<StickyNodeData, 'sticky'>;

/** Seeded lift value (0.4–1.5) from node ID so each note sits at a slightly different height. */
function stickyLift(id: string): number {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (Math.imul(31, h) + id.charCodeAt(i)) | 0;
  return 0.4 + (Math.abs(h % 1000) / 1000) * 1.1;
}

/**
 * Collaborative sticky note node. data.text is a Y.Text (live ref from
 * useNodesFromYjs). Writes are tagged with doc.clientID origin so the
 * per-user UndoManager (Task 12) tracks them correctly.
 *
 * Raw-HTML exception: React Flow v12 expects <div> containers for custom
 * nodes. The contenteditable editor is a structurally-required exception
 * documented in engine/README §Raw-HTML exception.
 */
export default memo(function StickyNode({ id, data, selected, dragging, parentId }: NodeProps<StickyNodeShape>) {
  // Merge canonical defaults — the bridge / legacy docs / fixtures may omit
  // formatting fields, and missing values produce invalid CSS in the
  // template literals below. Defaults provide the floor; data overrides.
  // `text` stays the passed Y.Text instance (or string from defaults) and
  // is normalised to Y.Text|null below.
  const d = withDefaults<StickyNodeData>(data, makeDefault as unknown as () => StickyNodeData);
  const { text: rawText, fill, fillOpacity, stroke, strokeOpacity, textAlign, verticalAlign, fontSize, fontFamily, bold, italic, underline, strikethrough, textColor } = d;
  const text = rawText instanceof Y.Text ? rawText : null;
  const actions = useNodeActions();
  const doc = useCanvasDoc();
  const lift = stickyLift(id);
  const [render, setRender] = useState(text?.toString() ?? '');
  // editing = true means the contenteditable is active; false = drag mode.
  // Double-click enters edit mode; clicking away (blur) exits it.
  const [editing, setEditing] = useState(false);
  const parentLabel = useParentLabelResolver();
  const ariaLabel = resolveNodeAriaLabel(
    { type: 'sticky', data: { text: render }, parentId: parentId ?? null },
    parentLabel,
  );

  // Keep render state in sync for aria-label updates.
  useEffect(() => {
    if (!text) return;
    const refresh = () => setRender(text.toString());
    text.observe(refresh);
    return () => text.unobserve(refresh);
  }, [text]);

  // Exit edit mode when node is deselected.
  useEffect(() => {
    if (!selected) setEditing(false);
  }, [selected]);

  if (!text) return null;

  const ghost = (
    <div
      className={styles.sticky}
      style={{
        '--sticky-color': fill,
        '--sticky-fill-opacity': fillOpacity,
        '--sticky-stroke': stroke === 'none' ? 'transparent' : stroke,
        '--sticky-stroke-opacity': strokeOpacity,
        '--sticky-lift': 0,
      } as React.CSSProperties}
    />
  );

  return (
    <SelectionTransformer
      nodeId={id}
      nodeType="sticky"
      selected={!!selected}
      dragging={!!dragging}
      editing={editing}
      onExpand={actions?.onExpand}
      ghostContent={ghost}
      doc={doc ?? undefined}
    >
      {/* React Flow node container — see engine/README §Raw-HTML exception */}
      <div
        className={styles.sticky}
        data-testid="sticky-node"
        data-node-id={id}
        data-parent-id={parentId ?? undefined}
        data-selected={selected || undefined}
        data-editing={editing || undefined}
        aria-label={ariaLabel}
        style={{
          '--sticky-color': fill,
          '--sticky-fill-opacity': fillOpacity,
          '--sticky-stroke': stroke === 'none' ? 'transparent' : stroke,
          '--sticky-stroke-opacity': strokeOpacity,
          '--sticky-font-size': `${fontSize}px`,
          '--sticky-lift': lift,
          ...formattingVars({ fontSize, fontFamily, textAlign, verticalAlign, bold, italic, underline, strikethrough, textColor }),
        } as React.CSSProperties}
        onDoubleClick={() => setEditing(true)}
      >
        <NodeTextEditor
          ytext={text}
          editing={editing}
          ariaLabel="sticky note text"
          className={editing ? `${styles.editor} ${fmtStyles.formattingText} nodrag` : `${styles.editor} ${fmtStyles.formattingText}`}
          onBlur={() => setEditing(false)}
        />
      </div>
    </SelectionTransformer>
  );
});
